import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/* Bootstrap's collapse slide, driven from React.
 *
 * The reference admin uses Bootstrap's `collapse` (the rules modal's accordion:
 * `<div class="collapse show">` toggled by `data-toggle="collapse"`) and
 * metisMenu's twin of it for the sidebar. The CSS for both is already shipped
 * in `src/styles/app.css`:
 *
 *     .collapse:not(.show) { display:none }
 *     .collapsing { position:relative; height:0; overflow:hidden;
 *                   transition:height .35s ease }
 *
 * The slide itself is NOT in that CSS — `collapsing` only supplies the
 * transition; Bootstrap's JS adds the class and animates `height` from 0 to
 * the content height and back. Flipping `show` straight from React (or
 * mounting/unmounting the content) snaps it open with no middle frame; the
 * reference recording (2026-09-18) shows the rules text growing into view.
 *
 * So this component owns the four-step dance Bootstrap's JS does, and nothing
 * else — no CSS is written here, the timing comes from the ported rule, and
 * DURATION_MS only exists as the fallback for a `transitionend` that never
 * fires (reduced motion, a tab backgrounded mid-animation):
 *
 *   closed   → collapse                         display:none
 *   opening  → collapsing, height 0 → full
 *   open     → collapse show                    display:block, no inline height
 *   preclose → collapse show, height = full px  ← the closing fix
 *   closing  → collapsing, height full → 0
 *
 * `preclose` exists because closing used to snap. Going straight from `open` to
 * `collapsing` means the element's height goes from `auto` to the class's own
 * `height:0` in one style recalculation, and `auto` is not interpolatable — the
 * browser has no start value to animate from, so it jumps shut. Pinning the
 * measured pixel height while the row is still `show`, and only switching the
 * class on the NEXT frame, gives the transition a real number to leave from.
 * Opening never had this problem: it starts from a class-supplied height:0.
 *
 * `classNames` swaps the three class names so the sidebar's metisMenu variant
 * (`mm-collapse` / `mm-collapsing` / `mm-show`, see MmCollapse.jsx) shares this
 * exact logic instead of carrying a copy.
 */

const DURATION_MS = 350 // keep in step with .collapsing's .35s

const BS_CLASSES = { collapse: 'collapse', collapsing: 'collapsing', show: 'show' }

export default function Collapse({
  open,
  as: Tag = 'div',
  classNames = BS_CLASSES,
  className = '',
  children,
  ...rest
}) {
  const ref = useRef(null)
  const [phase, setPhase] = useState(open ? 'open' : 'closed')
  const [height, setHeight] = useState(null)

  // A flip of `open` starts a transition; a flip back mid-flight reverses it.
  useEffect(() => {
    if (open) {
      if (phase === 'closed' || phase === 'preclose' || phase === 'closing') setPhase('opening')
    } else if (phase === 'open' || phase === 'opening') {
      setPhase('preclose')
    }
  }, [open, phase])

  // useLayoutEffect, not useEffect: every height below has to be committed
  // before the browser paints, or the row flashes at the wrong size first.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    let raf1 = 0
    let raf2 = 0
    let settle = 0

    if (phase === 'preclose') {
      // Still open and laid out — this is the only moment the real height is
      // readable without the collapsing class fighting for it.
      setHeight(el.scrollHeight)
      raf1 = requestAnimationFrame(() => setPhase('closing'))
    } else if (phase === 'opening') {
      setHeight(0)
      raf1 = requestAnimationFrame(() => {
        // Second frame: the start height is painted, so the target one animates.
        raf2 = requestAnimationFrame(() => setHeight(el.scrollHeight))
      })
      settle = setTimeout(() => setPhase('open'), DURATION_MS + 60)
    } else if (phase === 'closing') {
      // Height is already the pixel value pinned by `preclose`.
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setHeight(0))
      })
      settle = setTimeout(() => setPhase('closed'), DURATION_MS + 60)
    }

    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      clearTimeout(settle)
    }
  }, [phase])

  // A resting row carries no inline height, so content that grows later (a
  // nested accordion opening inside it) is not pinned to an old number.
  useEffect(() => {
    if (phase === 'open' || phase === 'closed') setHeight(null)
  }, [phase])

  const animating = phase === 'opening' || phase === 'closing'
  const shown = phase === 'open' || phase === 'preclose'
  const classes = [
    className,
    animating ? classNames.collapsing : classNames.collapse,
    shown ? classNames.show : '',
  ].filter(Boolean).join(' ')

  return (
    <Tag
      ref={ref}
      className={classes}
      style={height != null ? { height } : undefined}
      onTransitionEnd={(e) => {
        if (e.target !== e.currentTarget || e.propertyName !== 'height') return
        if (phase === 'opening') setPhase('open')
        else if (phase === 'closing') setPhase('closed')
      }}
      {...rest}
    >
      {children}
    </Tag>
  )
}
